import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { IconDatabaseOff, IconRefresh } from '@tabler/icons-react';
import Editor from '@monaco-editor/react';
import type { TaskListCatalog } from '@techgitter/contracts';
import { getTaskLists, putTaskLists, clearTaskListsCache } from '../services/taskListsService';
import { TaskListGuiEditor } from '../components/task-lists/TaskListGuiEditor';
import { EditorModeToggle } from '../components/admin/EditorModeToggle';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import { CARD_SX } from '../styles/designSystem';
import { useJsonGuiToggle, type EditorMode } from '../hooks/useJsonGuiToggle';

interface ValidationError {
  message: string;
}

function validateCatalog(data: unknown): ValidationError[] {
  const errors: ValidationError[] = [];
  if (typeof data !== 'object' || data === null) {
    return [{ message: 'Root must be a JSON object' }];
  }
  const obj = data as Record<string, unknown>;
  if (!('schemaVersion' in obj)) {
    errors.push({ message: 'Missing required field: schemaVersion' });
  }
  const taskLists = obj.taskLists;
  if (!Array.isArray(taskLists)) {
    errors.push({ message: 'taskLists must be an array' });
    return errors;
  }
  const seenIds = new Set<string>();
  for (let i = 0; i < taskLists.length; i++) {
    const tl = taskLists[i];
    if (typeof tl !== 'object' || tl === null) {
      errors.push({ message: `taskLists[${i}] must be an object` });
      continue;
    }
    const id = (tl as Record<string, unknown>).taskListId;
    if (!id || typeof id !== 'string') {
      errors.push({ message: `taskLists[${i}] missing taskListId` });
    } else if (seenIds.has(id)) {
      errors.push({ message: `Duplicate taskListId: ${id}` });
    } else {
      seenIds.add(id);
    }
    if (!(tl as Record<string, unknown>).displayName) {
      errors.push({ message: `taskLists[${i}] missing displayName` });
    }
    const sections = (tl as Record<string, unknown>).sections;
    if (sections !== undefined && !Array.isArray(sections)) {
      errors.push({ message: `taskLists[${i}].sections must be an array` });
    }
  }
  return errors;
}

export const TaskListsAdminPage: React.FC = () => {
  const [catalog, setCatalog] = useState<TaskListCatalog>({ schemaVersion: 1, taskLists: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [clearing, setClearing] = useState(false);

  const { mode, jsonText, setJsonText, switchMode, syncJsonFromValue } = useJsonGuiToggle<TaskListCatalog>({
    value: catalog,
    onChange: setCatalog,
    initialMode: 'gui',
    validate: (parsed) => validateCatalog(parsed).map((e) => e.message),
  });

  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTaskLists(signal);
      setCatalog(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load task lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  // Resync JSON text after a network-driven catalog change (load/save).
  useEffect(() => {
    if (mode === 'gui') syncJsonFromValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  const handleSelectMode = (newMode: EditorMode) => {
    const result = switchMode(newMode);
    if (!result.ok) setError(result.error ?? 'Cannot switch modes');
  };

  const handleCatalogChange = (updated: TaskListCatalog) => {
    setCatalog(updated);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setValidationErrors([]);

    let dataToSave: TaskListCatalog;

    if (mode === 'json') {
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonText);
      } catch {
        setError('Invalid JSON');
        return;
      }

      const errors = validateCatalog(parsed);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      dataToSave = parsed as TaskListCatalog;
    } else {
      const errors = validateCatalog(catalog);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      dataToSave = catalog;
    }

    setSaving(true);
    try {
      const saved = await putTaskLists(dataToSave);
      setCatalog(saved);
      // Best-effort cache-clear so the dev/server cache stays consistent.
      clearTaskListsCache().catch(() => {});
      setSuccess('Task lists saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClearCache = async () => {
    setClearing(true);
    try {
      const result = await clearTaskListsCache();
      setSuccess(result.cleared ? 'Cache cleared' : 'Cache was already empty');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cache');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const feedback = (error || success || validationErrors.length > 0) ? (
    <>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Validation errors:</Typography>
          {validationErrors.map((e, i) => (
            <Typography key={i} variant="body2">- {e.message}</Typography>
          ))}
        </Alert>
      )}
    </>
  ) : null;

  return (
    <>
      {feedback && <Box sx={{ px: '16px', pt: '10px' }}>{feedback}</Box>}

      {mode === 'gui' ? (
        <TaskListGuiEditor
          mode={mode}
          onModeChange={handleSelectMode}
          catalog={catalog}
          onCatalogChange={handleCatalogChange}
          onSave={handleSave}
          onClearCache={handleClearCache}
          onReload={() => loadData()}
          saving={saving}
          clearing={clearing}
        />
      ) : (
        <Box>
          <SecondaryToolbar floating>
            <EditorModeToggle mode={mode} onChange={handleSelectMode} />
            <ToolbarSpacer />
            <ToolbarButton variant="primary" onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </ToolbarButton>
            <ToolbarButton icon={IconDatabaseOff} onClick={handleClearCache}>
              {clearing ? 'Clearing…' : 'Clear Cache'}
            </ToolbarButton>
            <ToolbarButton icon={IconRefresh} onClick={() => loadData()}>
              Reload
            </ToolbarButton>
          </SecondaryToolbar>
          <Box sx={{ p: '12px 16px 24px' }}>
            <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
              <Editor
                height="70vh"
                defaultLanguage="json"
                value={jsonText}
                onChange={(value) => setJsonText(value ?? '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  formatOnPaste: true,
                  scrollBeyondLastLine: false,
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
